import React, { useState } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { EmptyState, ErrorState, LoadingState, PageTitle, StatusChip, useAsync } from '../PortalUi';
import { fetchDeposits } from '../../../../services/request/CustomerPortal';
import { formatDate, inr } from '../../../../utils/customerPortalFormat';
import { useTranslation } from '../../../../i18n';

export default function PortalDeposits() {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const { t } = useTranslation();
  const deposits = useAsync(() => fetchDeposits(page, size), [page, size]);

  return (
    <Box>
      <PageTitle kicker={t('portal.deposits.kicker')} title={t('portal.deposits.title')} subtitle={t('portal.deposits.subtitle')} />
      {deposits.data?.totalActiveAmount !== undefined ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography color="text.secondary">{t('portal.deposits.activeBalance')}</Typography>
          <Typography variant="h4">{inr(deposits.data.totalActiveAmount)}</Typography>
        </Paper>
      ) : null}
      {deposits.error ? <ErrorState message={deposits.error} onRetry={deposits.reload} /> : null}
      {deposits.loading && !deposits.data ? <LoadingState label={t('portal.deposits.loading')} /> : null}
      {deposits.data && deposits.data.totalCount === 0 ? <EmptyState title={t('portal.deposits.empty')} /> : null}
      {deposits.data && deposits.data.totalCount > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('portal.common.columns.depositDate')}</TableCell>
                <TableCell align="right">{t('portal.common.columns.amount')}</TableCell>
                <TableCell>{t('portal.common.columns.maturity')}</TableCell>
                <TableCell>{t('portal.common.columns.status')}</TableCell>
                <TableCell>{t('portal.common.columns.reference')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deposits.data.items.map(deposit => (
                <TableRow key={deposit.id} hover>
                  <TableCell>{formatDate(deposit.depositDate) ?? '—'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {inr(deposit.amount)}
                  </TableCell>
                  <TableCell>{formatDate(deposit.maturityDate) ?? '—'}</TableCell>
                  <TableCell>
                    <StatusChip status={deposit.status} />
                  </TableCell>
                  <TableCell>{deposit.referenceNumber || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={deposits.data.totalCount}
            page={page}
            rowsPerPage={size}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage={t('portal.common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('portal.common.rowsRange', { from, to, count })}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={event => {
              setSize(Number(event.target.value));
              setPage(0);
            }}
          />
        </TableContainer>
      ) : null}
    </Box>
  );
}
